import { faEdit, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { useQuery } from '@tanstack/react-query';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { TextField } from 'src/components/TextField';
import { fetchLocations } from 'src/models/location/location';
import { BaseSelect } from 'src/shared/Forms/Selects/BaseSelect';

import { FilterTypeComponentProps } from './types';

export const FilterTypeLocation: FC<FilterTypeComponentProps> = ({
  value,
  onChange,
  onDelete,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'reminderFilters.filterSet',
  });

  const { data: locations = [] } = useQuery({
    queryKey: ['locations'],
    queryFn: () =>
      fetchLocations({
        fetchAll: true,
        sort: 'name',
        sortDir: 'asc',
      }),
    select: (data) => data.locations,
    staleTime: 60000,
    gcTime: 60000,
  });

  const [editing, setEditing] = useState(false);

  const handleToggle = () => setEditing((e) => !e);

  return (
    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
      {editing ? (
        <>
          {t('are')}
          <TextField
            size="medium"
            value={value.negate ? 'not' : 'is'}
            onChange={(e) =>
              onChange({ ...value, negate: e.target.value === 'not' })
            }
            options={[
              { value: 'is', label: t('assigned') },
              { value: 'not', label: t('notAssigned') },
            ]}
            sx={{ minWidth: 100 }}
          />
          {t('to')}
          <BaseSelect
            value={value.value ?? ''}
            onChange={(v) => onChange({ ...value, value: v || null })}
            options={locations.map((g) => ({
              id: g.id.toString(),
              label: g.name,
            }))}
            placeholder={t('anyLocation')}
            sx={{ minWidth: 200 }}
          />
          <Button color="primary" variant="prominent" onClick={handleToggle}>
            {t('done')}
          </Button>
        </>
      ) : (
        <>
          {t('are')} {value.negate && t('not')}{' '}
          {value.value
            ? `${t('assigned')} ${t('to')} ${
                locations.find((l) => String(l.id) === String(value.value))?.name || t('unknownLocation', { defaultValue: 'Unknown location' })
              }`
            : `${t('assigned')} ${t('to')} ${t('anyLocation').toLowerCase()}`}
          <IconButton size="small" color="primary" onClick={handleToggle}>
            <Icon icon={faEdit} size="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => onDelete()}>
            <Icon icon={faTrash} size="small" />
          </IconButton>
        </>
      )}
    </Box>
  );
};
